import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

import reviewQuery from "@src/queries/reviewQuery";
import {
  EnumReviewSortType,
  EnumReviewType,
  IResPartnerReviewItem,
  SelectedTag,
} from "@src/api/review/types";
import { flattenInfiniteQueryResult } from "@src/queries/utils";
import { getImageUrlWithMaxWidth, isExternalUrl, HOSTNAME } from "@src/utils";
import toast from "@src/utils/toast";
import { getConvertedReviewItemTagName } from "@src/pages/review/_util";

import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
import Icon from "@src/components/common/icons/Icon";
import NoData from "@src/components/common/noData/NoData";
import InfiniteScrollWithObserver from "@src/components/common/infiniteScroll/InfiniteScrollWithObserver";
import ReviewListOptionBox from "./_component/ReviewListOptionBox";
import ReviewActionModal from "@src/pages/review/_component/detail/ActionModal";

import WeddingBookMarkImg from "@src/assets/images/icon/img_weddingbook.png";
import BlogMarkImg from "@src/assets/images/icon/img_blog.png";

import styles from "./partnerReviewList.module.scss";

const DEFAULT_FETCH_SIZE = 10;

export default function PartnerReviewList(props: { partnerUuid: string; partnerType: number }) {
  const [filterValue, setFilterValue] = useState<SelectedTag | null>(null);
  const [sortValue, setSortValue] = useState<EnumReviewSortType>(EnumReviewSortType.NEWEST);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [reviewList, setReviewList] = useState<IResPartnerReviewItem[]>([]);

  const { data, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    reviewQuery.useGetInfinityPartnerReviewList({
      fetchStart: 0,
      fetchSize: DEFAULT_FETCH_SIZE,
      sortType: sortValue,
      partnerUuid: props.partnerUuid,
      filterType: filterValue,
    });

  // useInfiniteQuery로 받아온 데이터 정리
  useEffect(() => {
    if (data === undefined) return;

    const { total, list } = flattenInfiniteQueryResult<IResPartnerReviewItem>(data);
    setTotalCount(total);
    setReviewList(list);
  }, [data]);

  // 에러 처리
  useEffect(() => {
    if (isError === true) {
      toast.showErrorSnackbar("리뷰 정보를 가져오는데 실패하였습니다.");
    }
  }, [isError]);

  return (
    <div className={styles["partner-review-list"]}>
      <div className={styles["title-box"]}>
        <h2>리뷰</h2>

        {totalCount !== null && (
          <span className={styles["count"]}>{totalCount.toLocaleString()}</span>
        )}
      </div>

      <ReviewListOptionBox
        partnerType={props.partnerType}
        filterValue={filterValue}
        onChangeFilterValue={(value) => setFilterValue(value)}
        sortValue={sortValue}
        onChangeSortValue={(value) => setSortValue(value)}
      />

      {data === undefined && <LoadingSpinner position="relative" height={240} />}

      {data !== undefined && reviewList.length > 0 && (
        <ReviewList
          items={reviewList}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}

      {data !== undefined && reviewList.length === 0 && (
        <NoData title={"아직 작성된 리뷰가 없습니다."} desc={"리뷰를 작성해보세요."} />
      )}
    </div>
  );
}

function ReviewList(props: {
  items: IResPartnerReviewItem[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}) {
  const [targetCommunityUuid, setTargetCommunityUuid] = useState<string | null>(null);
  const [targetCustomerUuid, setTargetCustomerUuid] = useState<string | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);

  function openReportModal(communityUuid: string, customerUuid: string) {
    setTargetCommunityUuid(communityUuid);
    setTargetCustomerUuid(customerUuid);
    setIsActionModalOpen(true);
  }
  function closeReportModal() {
    setTargetCommunityUuid(null);
    setTargetCustomerUuid(null);
    setIsActionModalOpen(false);
  }

  return (
    <>
      <div className={styles["review-list"]}>
        <InfiniteScrollWithObserver
          fetchNextPage={props.fetchNextPage}
          hasNextPage={props.hasNextPage}
          isFetchingNextPage={props.isFetchingNextPage}
          observerPositionFromBottom={120}
        >
          {props.items.map((item) => (
            <ReviewItem
              key={item.reviewId}
              item={item}
              onClickMoreButton={() => openReportModal(item.communityUuid, item.customerUuid)}
            />
          ))}
        </InfiniteScrollWithObserver>

        <ReviewActionModal
          isOpen={isActionModalOpen}
          onClose={closeReportModal}
          communityUuid={targetCommunityUuid}
          customerUuid={targetCustomerUuid}
        />
      </div>
    </>
  );
}

function ReviewItem(props: { item: IResPartnerReviewItem; onClickMoreButton: () => void }) {
  const { item, onClickMoreButton } = props;

  const contentRef = useRef<HTMLParagraphElement>(null);

  const [isTextLineOverflow, setIsTextLineOverflow] = useState(false);
  const [hasTextOrImageContent, setHasTextOrImageContent] = useState(false);

  // 텍스트 또는 이미지 존재 여부 체크
  useEffect(() => {
    if (item.content.length > 0 || item.images.length > 0) {
      setHasTextOrImageContent(true);
    } else {
      setHasTextOrImageContent(false);
    }
  }, [item.content, item.images]);

  // 글자 3줄 넘어가는지 체크
  useEffect(() => {
    if (contentRef.current === null) return;

    const lineHeight = parseInt(getComputedStyle(contentRef.current).lineHeight);
    const maxHeight = lineHeight * 3;
    const isContentOverflowing = contentRef.current.scrollHeight > maxHeight;
    setIsTextLineOverflow(isContentOverflowing);
  }, [item.content]);

  // 리뷰 상세 페이지 이동
  function goToReviewDetail(reviewId: number, reviewType: string) {
    window.location.href = isExternalUrl(
      `${HOSTNAME}/review/detail/${reviewId}?reviewType=${reviewType}`,
    );
  }

  function getFormattedDate(date: string) {
    return dayjs(date).format("YY.MM.DD");
  }

  return (
    <div className={styles["review-item"]}>
      <div
        className={styles["inner"]}
        onClick={() => {
          if (item.isBan === true) return;
          goToReviewDetail(item.reviewId, item.reviewType);
        }}
      >
        <div className={styles["item-header"]}>
          <div className={styles["rating-box"]}>
            {Array.from({ length: item.rating }, (_, index) => (
              <Icon key={index} name={"Star"} size={14} />
            ))}
          </div>
          <p className={styles["writer-name"]}>{item.writerName}</p>
          <div className={styles["date"]}>{getFormattedDate(item.regTsp)}</div>
        </div>

        {item.isBan === true && <p className={styles["item-ban-review"]}>차단된 리뷰입니다.</p>}

        {item.isBan === false && item.content.length > 0 && (
          <div className={styles["item-content-box"]}>
            <p ref={contentRef} className={`${styles["item-content"]}`}>
              {item.content}
            </p>
            {isTextLineOverflow === true && (
              <button className={`btn ${styles["text-more-button"]}`}>더보기</button>
            )}
          </div>
        )}

        {item.isBan === false && item.images.length > 0 && (
          <div className={styles["item-images-box"]}>
            {item.images.map((image: string, index: number) => (
              <div key={index} className={styles["image-box"]}>
                <img
                  src={getImageUrlWithMaxWidth(image, 180)}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {item.isBan === false && hasTextOrImageContent === true && (
        <div className={styles["item-footer"]}>
          <div className={styles["type-box"]}>
            <img
              src={
                item.reviewType === EnumReviewType.WEDDINGBOOK_REVIEW
                  ? WeddingBookMarkImg
                  : BlogMarkImg
              }
              alt=""
            />
          </div>
          <div className={styles["tag"]}>
            {getConvertedReviewItemTagName(item.reviewTagName as SelectedTag)}
          </div>

          {item.isMyReview === false && item.reviewType === EnumReviewType.WEDDINGBOOK_REVIEW && (
            <button className={`btn ${styles["more-button"]}`} onClick={onClickMoreButton}>
              <Icon name="MoreHorizontal" size={18} />
            </button>
          )}
        </div>
      )}

      <div className={styles["review-item-divider"]} />
    </div>
  );
}
